import { adminApi } from "./adminApi";

/**
 * @typedef {"no_section_start_after_signup" | "started_not_completed" | "dormant_user"} AlertRuleType
 * @typedef {"daily" | "weekly"} AlertSchedule
 *
 * @typedef {Object.<string, unknown>} AnalyticsSummary
 *
 * @typedef {Object} ActivationReportResponse
 * @property {AnalyticsSummary=} summary
 * @property {Array<Object>=} users
 *
 * @typedef {Object} StartCompletionReportResponse
 * @property {AnalyticsSummary=} summary
 * @property {Array<Object>=} users
 * @property {Array<Object>=} sections
 *
 * @typedef {Object} RetentionReportResponse
 * @property {AnalyticsSummary=} summary
 * @property {Array<Object>=} dormant_users
 *
 * @typedef {Object} ProgressionFunnelResponse
 * @property {AnalyticsSummary=} summary
 * @property {Object=} course
 * @property {Array<Object>=} stages
 *
 * @typedef {Object} AlertRule
 * @property {string|number=} id
 * @property {string|number=} rule_id
 * @property {string=} name
 * @property {AlertRuleType=} rule_type
 * @property {boolean=} enabled
 * @property {number=} threshold_days
 * @property {AlertSchedule=} schedule
 * @property {Object.<string, unknown>=} filters
 * @property {string=} last_run_at
 * @property {string=} email_subject
 * @property {string=} email_subject_template
 * @property {string=} email_body_template
 * @property {string|null=} email_html_template
 * @property {string|null=} cta_label
 * @property {string|null=} cta_url
 * @property {number|null=} max_deliveries_per_user
 *
 * @typedef {Object} AlertDelivery
 * @property {string=} status
 * @property {string=} email
 * @property {string=} sent_at
 * @property {string=} error_message
 */

/**
 * @param {Object} params
 * @param {number=} params.days_without_start
 * @param {number=} params.limit
 * @param {number=} params.offset
 * @param {boolean=} params.paid_only
 * @returns {Promise<ActivationReportResponse|Object>}
 */
const getActivationReport = async (params = {}) => {
  const res = await adminApi.get("/analytics/reports/activation", { params });
  return res.data;
};

/**
 * @param {Object} params
 * @param {number=} params.stale_days
 * @param {number=} params.limit
 * @param {number=} params.offset
 * @param {boolean=} params.paid_only
 * @returns {Promise<StartCompletionReportResponse|Object>}
 */
const getStartCompletionReport = async (params = {}) => {
  const res = await adminApi.get("/analytics/reports/start-completion", {
    params,
  });
  return res.data;
};

/**
 * @param {Object} params
 * @param {number=} params.dormant_days
 * @param {number=} params.cohort_days
 * @param {boolean=} params.paid_only
 * @returns {Promise<RetentionReportResponse|Object>}
 */
const getRetentionReport = async (params = {}) => {
  const res = await adminApi.get("/analytics/reports/retention", { params });
  return res.data;
};

/**
 * @param {Object} params
 * @param {boolean=} params.paid_only
 * @param {string=} params.course_id
 * @param {string=} params.course_slug
 * @returns {Promise<ProgressionFunnelResponse|Object>}
 */
const getProgressionFunnelReport = async (params = {}) => {
  const res = await adminApi.get("/analytics/reports/progression-funnel", {
    params,
  });
  return res.data;
};

/**
 * @returns {Promise<Array<AlertRule>|{items?: Array<AlertRule>}>}
 */
const getAlertRules = async () => {
  const res = await adminApi.get("/analytics/alerts");
  return res.data;
};

/**
 * @param {Partial<AlertRule>} payload
 * @returns {Promise<AlertRule|Object>}
 */
const createAlertRule = async (payload) => {
  const res = await adminApi.post("/analytics/alerts", payload);
  return res.data;
};

/**
 * @param {string|number} ruleId
 * @param {Partial<AlertRule>} payload
 * @returns {Promise<AlertRule|Object>}
 */
const updateAlertRule = async (ruleId, payload) => {
  const res = await adminApi.patch(`/analytics/alerts/${ruleId}`, payload);
  return res.data;
};

/**
 * @param {string|number} ruleId
 * @returns {Promise<Object>}
 */
const runAlertRule = async (ruleId) => {
  const res = await adminApi.post(`/analytics/alerts/${ruleId}/run`);
  return res.data;
};

/**
 * @returns {Promise<Object>}
 */
const runEnabledAlertRules = async () => {
  const res = await adminApi.post("/analytics/alerts/run-enabled");
  return res.data;
};

/**
 * @param {string|number} ruleId
 * @param {Object} params
 * @param {number=} params.limit
 * @param {number=} params.offset
 * @returns {Promise<Array<AlertDelivery>|{items?: Array<AlertDelivery>}>}
 */
const getAlertDeliveries = async (ruleId, params = {}) => {
  const res = await adminApi.get(`/analytics/alerts/${ruleId}/deliveries`, {
    params,
  });
  return res.data;
};

export const adminAnalyticsService = {
  getActivationReport,
  getStartCompletionReport,
  getRetentionReport,
  getProgressionFunnelReport,
  getAlertRules,
  createAlertRule,
  updateAlertRule,
  runAlertRule,
  runEnabledAlertRules,
  getAlertDeliveries,
};

